import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreProvider';
import { Heart, X } from 'lucide-react';
import alertify from 'alertifyjs';

const ProductDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, toggleFavorite, addToCart } = useStore();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [sizeGuideData, setSizeGuideData] = useState(null);
  const [isLoadingSizeGuide, setIsLoadingSizeGuide] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

  const fetchSizeGuide = async () => {
    if (!product || !product.category) return;
    setShowSizeGuide(true);
    setIsLoadingSizeGuide(true);
    try {
      const res = await fetch(`${API_BASE}/api/store/size-guides/?category=${product.category}`);
      if (res.ok) {
        const data = await res.json();
        const results = data.results !== undefined ? data.results : data;
        
        if (results && results.length > 0) {
          setSizeGuideData(results[0]);
        } else {
          setSizeGuideData(null);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoadingSizeGuide(false);
    }
  };

  useEffect(() => {
    const p = products.find(prod => prod.id === id);
    if (p) {
      setProduct(p);
      setActiveImage(p.images && p.images.length > 0 ? p.images[0].image : null);
      if (p.variants && p.variants.length > 0) {
        setSelectedVariant(p.variants[0]);
      }
    }
  }, [id, products]);

  if (!product) {
    return (
      <div className="flex justify-center items-center py-40">
        <div className="text-[#13110E]/50 font-serif italic">Loading product details...</div>
      </div>
    );
  }

  const allImages = (product.images || []).map(img => img.image).filter(Boolean);

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        
        {/* Left Column: Images */}
        <div className="flex flex-col space-y-4">
          <div className="relative aspect-[4/5] bg-[#E5DFD5] w-full overflow-hidden">
            {activeImage ? (
              <img src={activeImage} alt={product.name} className="w-full h-full object-cover object-center" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#13110E]/30 font-serif italic">No Image</div>
            )}
          </div>
          {allImages.length > 1 && (
            <div className="grid grid-cols-4 gap-4">
              {allImages.map((imgUrl, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(imgUrl)}
                  className={`relative aspect-[4/5] bg-[#E5DFD5] overflow-hidden border transition-colors ${
                    activeImage === imgUrl ? 'border-[#13110E]' : 'border-transparent hover:border-[#13110E]/30'
                  }`}
                >
                  <img src={imgUrl} alt={`Thumbnail ${idx}`} className="w-full h-full object-cover object-center" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Column: Details */}
        <div className="flex flex-col py-4 lg:py-10">
          <span className="text-[9px] tracking-[0.3em] text-[#13110E]/60 uppercase mb-2">
            {product.category_detail?.name || 'AFRICAN WEAR'}
          </span>
          <h1 className="text-5xl md:text-7xl font-serif text-[#13110E] mb-4">
            {product.name}
          </h1>
          <p className="text-2xl font-serif text-[#13110E] mb-10 flex flex-col">
            <span>
              ₵{((selectedVariant ? parseFloat(selectedVariant.price) : parseFloat(product.price)) * quantity).toFixed(2)}
            </span>
            {product.was_price && !selectedVariant && (
              <span className="line-through text-gray-400 text-lg mt-1">
                ₵{(parseFloat(product.was_price) * quantity).toFixed(2)}
              </span>
            )}
          </p>

          <div className="border-b border-[#13110E]/10 mb-10"></div>

          <p className="font-serif italic text-lg text-[#13110E]/80 mb-12">
            {product.description || 'A nice african wear made with best fabric'}
          </p>

          {product.colors_detail && product.colors_detail.length > 0 && (
            <div className="mb-12">
              <div className="mb-4 text-[9px] tracking-[0.2em] uppercase text-[#13110E]/60 font-medium">
                COLORS
              </div>
              <div className="flex flex-wrap gap-3">
                {product.colors_detail.map((color, idx) => (
                  <div 
                    key={idx} 
                    className="w-8 h-8 rounded-full border border-[#13110E]/20 shadow-sm"
                    style={{ backgroundColor: color.color_code }}
                    title={color.name}
                  ></div>
                ))}
              </div>
            </div>
          )}

          {product.variants && product.variants.length > 0 && (
            <div className="mb-12">
              <div className="flex justify-between items-center mb-4 text-[9px] tracking-[0.2em] uppercase text-[#13110E]/60 font-medium">
                <span>SELECT SIZE</span>
                <button 
                  onClick={fetchSizeGuide}
                  className="hover:text-[#13110E] border-b border-transparent hover:border-[#13110E] transition-colors pb-0.5"
                >
                  SIZE GUIDE
                </button>
              </div>
              <div className="flex flex-wrap gap-4">
                {product.variants.map(variant => (
                  <button
                    key={variant.id}
                    onClick={() => { setSelectedVariant(variant); setQuantity(1); }}
                    className={`px-8 py-3 text-[10px] tracking-[0.2em] uppercase transition-colors ${
                      selectedVariant?.id === variant.id 
                        ? 'bg-[#13110E] text-[#EBE6DD]' 
                        : 'border border-[#13110E]/20 text-[#13110E] hover:border-[#13110E]'
                    }`}
                  >
                    {variant.size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Action Row */}
          <div className="flex items-stretch gap-4 mb-6">
            <div className="flex items-center border border-[#13110E]/20 h-12">
              <button 
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 flex items-center justify-center text-[#13110E] hover:bg-[#13110E]/5 transition-colors"
              >
                −
              </button>
              <span className="w-8 text-center text-sm font-serif">{quantity}</span>
              <button 
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 flex items-center justify-center text-[#13110E] hover:bg-[#13110E]/5 transition-colors"
              >
                +
              </button>
            </div>

            <button
              onClick={() => {
                if (product.variants?.length > 0 && !selectedVariant) {
                  alertify.error('Please select a size first');
                  return;
                }
                addToCart(product, quantity, selectedVariant?.id);
                setQuantity(1); 
              }}
              className="flex-1 bg-[#13110E] text-[#EBE6DD] h-12 flex items-center justify-center text-[10px] tracking-[0.3em] font-medium hover:bg-[#13110E]/80 transition-colors"
            >
              ADD TO BAG
            </button>

            <button
              onClick={() => toggleFavorite(product.id)}
              className="w-12 h-12 flex items-center justify-center border border-[#13110E]/20 hover:border-[#13110E] transition-colors"
            >
              <Heart
                size={18}
                strokeWidth={1.5}
                className={product.isFavorite ? "fill-[#13110E] text-[#13110E]" : "text-[#13110E]"}
              />
            </button>
          </div>
          
          <span className="text-[8px] tracking-[0.2em] text-[#13110E]/50 uppercase mt-2">
            SHIPPING CALCULATED AT CHECKOUT
          </span>

        </div>
      </div>

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-[#13110E]/40 backdrop-blur-sm"
            onClick={() => setShowSizeGuide(false)}
          ></div>
          <div className="relative bg-[#EBE6DD] w-full max-w-4xl rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="flex justify-between items-center p-6 border-b border-[#13110E]/10">
              <h2 className="font-serif text-2xl text-[#13110E]">
                Size Guide {sizeGuideData?.category_name ? `- ${sizeGuideData.category_name}` : ''}
              </h2>
              <button 
                onClick={() => setShowSizeGuide(false)}
                className="p-2 text-[#13110E]/60 hover:text-[#13110E] hover:bg-[#13110E]/5 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {isLoadingSizeGuide ? (
                <div className="flex justify-center py-12 text-[#13110E]/50 font-serif italic">
                  Loading size guide...
                </div>
              ) : !sizeGuideData || !sizeGuideData.measurements || sizeGuideData.measurements.length === 0 ? (
                <div className="flex justify-center py-12 text-[#13110E]/50 font-serif italic">
                  No size guide available for this item.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="border-b-2 border-[#13110E]">
                        <th className="py-3 px-4 font-serif font-bold text-[#13110E]">Size</th>
                        {sizeGuideData.show_shoulder && <th className="py-3 px-4 font-serif font-bold text-[#13110E]">Shoulder to shoulder</th>}
                        {sizeGuideData.show_bust && <th className="py-3 px-4 font-serif font-bold text-[#13110E]">Bust</th>}
                        {sizeGuideData.show_waist && <th className="py-3 px-4 font-serif font-bold text-[#13110E]">Waist</th>}
                        {sizeGuideData.show_hip && <th className="py-3 px-4 font-serif font-bold text-[#13110E]">Hip</th>}
                        {sizeGuideData.show_length && <th className="py-3 px-4 font-serif font-bold text-[#13110E]">{sizeGuideData.length_label || 'Length'}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sizeGuideData.measurements.map((m, idx) => (
                        <tr key={idx} className="border-b border-[#13110E]/10 hover:bg-[#13110E]/5 transition-colors">
                          <td className="py-3 px-4 font-medium text-[#13110E]">{m.size_name}</td>
                          {sizeGuideData.show_shoulder && <td className="py-3 px-4 text-[#13110E]/80">{m.shoulder || '-'}</td>}
                          {sizeGuideData.show_bust && <td className="py-3 px-4 text-[#13110E]/80">{m.bust || '-'}</td>}
                          {sizeGuideData.show_waist && <td className="py-3 px-4 text-[#13110E]/80">{m.waist || '-'}</td>}
                          {sizeGuideData.show_hip && <td className="py-3 px-4 text-[#13110E]/80">{m.hip || '-'}</td>}
                          {sizeGuideData.show_length && <td className="py-3 px-4 text-[#13110E]/80">{m.length || '-'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-6 text-[10px] tracking-[0.1em] text-[#13110E]/50 uppercase text-center flex items-center justify-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 10 5 5-5 5"/><path d="M4 15h16"/><path d="m9 10-5 5 5 5"/></svg>
                    Scroll horizontally to see more measurements
                  </p>
                  <p className="mt-2 text-[10px] tracking-[0.1em] text-[#13110E]/50 uppercase text-center">
                    All measurements are in inches.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductDetailScreen;
